import React, { useCallback, useEffect, useState } from 'react';
import { Route, Routes, useLocation, Navigate } from 'react-router-dom';
import { ROUTES, CAREGIVER_ROUTES, PUBLIC_ROUTES, ADMIN_ROUTES } from './constants';
import { Loader, Sidebar } from 'components';
import { useAppDispatch, useAppSelector, useAuth0, useTheme } from 'hooks';
import { DataStatus, NotificationType, StorageKey, UserRole, RoutePath } from 'types/enums';
import { SignUp } from 'screens';
import { ProtectRoute } from 'components/protect-route/protect-route';
import styles from './styles.module.sass';
import { userActions } from 'utils/store';
import { notificationActions } from 'utils/store/slice/notification';
import { tokens } from 'theme';
import { setTokenHeader } from 'api/controllers/account-controller';
import { storage } from 'utils/util';

export const Main = () => {
  const { isAuthenticated: isAuth0Authenticated, user: auth0User, getAccessTokenSilently, isLoading } = useAuth0();
  const [isScreenLoading, setIsScreenLoading] = useState(false);
  const location = useLocation();
  const { data, status } = useAppSelector(({ user }) => user);
  const dispatch = useAppDispatch();
  const isUserLoading = status === DataStatus.LOADING || isLoading || isScreenLoading;
  const isEverbeatUserExist = data && isAuth0Authenticated;
  const isPartnerUserExist = data && data.role === UserRole.PARTNER;
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const publicRouteRegex = new RegExp(
    PUBLIC_ROUTES.map((route) => `^${route.path.replace(/:\w+/g, '[^/]+')}$`).join('|'),
  );

  const isPublicRoute = publicRouteRegex.test(location.pathname);
  const shouldShowPlaceholder = !isAuth0Authenticated && !isPartnerUserExist && !isPublicRoute;

  useEffect(() => {
    const checkPartnerLogin = async () => {
      const isPartnerLogin = await storage.get(StorageKey.IS_PARTNER_LOGIN);
      const partnerToken = await storage.get(StorageKey.PARTNER_TOKEN);

      if (isPartnerLogin === 'true' && partnerToken) {
        const existingData = await storage.get(StorageKey.PARTNER_DATA);

        if (existingData) {
          try {
            const parsedData = JSON.parse(existingData);
            dispatch(userActions.setUser(parsedData));
            dispatch(userActions.setToken(partnerToken));
            setTokenHeader(partnerToken);
          } catch (e) {
            console.error('Error parsing partner data:', e);
          }
        }
      }
    };

    checkPartnerLogin();
  }, [dispatch]);

  useEffect(() => {
    if (isAuth0Authenticated && auth0User?.email) {
      const getUser = async () => {
        setIsScreenLoading(true);
        const token = await getAccessTokenSilently();
        setTokenHeader(token);
        await storage.set(StorageKey.TOKEN, token);
        dispatch(userActions.setToken(token));
        await dispatch(userActions.getUser(auth0User.email));
      };

      try {
        getUser();
      } catch (e) {
        dispatch(notificationActions.notify({ message: 'Error getting user', type: NotificationType.ERROR }));
        console.error(e);
      } finally {
        setIsScreenLoading(false);
      }
    }
  }, [auth0User?.email, dispatch, getAccessTokenSilently, isAuth0Authenticated]);

  const getContent = useCallback(
    () => (
      <div className={styles.container}>
        <div className={styles.content} style={{ background: colors.dashboard.background }}>
          <Routes>
            {/* Add a catch-all route for the root path */}
            <Route
              path='/'
              element={
                isEverbeatUserExist || isPartnerUserExist ? (
                  <Navigate to={data?.role === UserRole.PARTNER ? RoutePath.ECG : RoutePath.PRACTICE} replace />
                ) : (
                  <h1 style={{ textAlign: 'center', width: '100%' }}>Please Log in</h1>
                )
              }
            />
            {PUBLIC_ROUTES.map(({ path, element }) => (
              <Route key={path} path={path} element={element} />
            ))}
            {(isAuth0Authenticated || isPartnerUserExist) &&
              (isEverbeatUserExist || isPartnerUserExist ? (
                <>
                  {/* Common routes accessible to all authenticated users */}
                  {ROUTES.map(({ path, element }) => (
                    <Route key={path} path={path} element={element} />
                  ))}

                  {/* Role-specific protected routes */}
                  <Route element={<ProtectRoute userRole={data?.role} accessRoles={[UserRole.CAREGIVER]} />}>
                    {CAREGIVER_ROUTES.map(({ path, element }) => (
                      <Route key={path} path={path} element={element} />
                    ))}
                  </Route>
                  <Route
                    element={<ProtectRoute userRole={data?.role} accessRoles={[UserRole.CAREGIVER, UserRole.ADMIN]} />}
                  >
                    {ADMIN_ROUTES.map(({ path, element }) => (
                      <Route key={path} path={path} element={element} />
                    ))}
                  </Route>
                </>
              ) : (
                <Route path='*' element={<SignUp />} />
              ))}
            {shouldShowPlaceholder && (
              <Route path='*' element={<h1 style={{ textAlign: 'center', width: '100%' }}>Please Log in</h1>} />
            )}
          </Routes>
        </div>
      </div>
    ),
    [
      colors.dashboard.background,
      isAuth0Authenticated,
      isEverbeatUserExist,
      isPartnerUserExist,
      data?.role,
      shouldShowPlaceholder,
    ],
  );

  return (
    <div className='app'>
      {!isPublicRoute && (isEverbeatUserExist || isPartnerUserExist) && <Sidebar />}
      {isUserLoading ? <Loader isVisible={isUserLoading} /> : getContent()}
    </div>
  );
};
